import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import GradientLoadingAnimation from 'components/Reusables/GradientLoadingAnimation'
import { debounce } from 'lodash'
import { Copy, Search, Trash } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { $fetch } from 'utils/fetch'
import { deleteInbound } from 'utils/funcs/byot/deleteInbound'
import { getInbound } from 'utils/funcs/byot/getInbound'
import { insertInbound } from 'utils/funcs/byot/insertInbound'
import ConfigurationSection from './BYOTConfig'

const DEFAULT_PAGINATION = {
  page: 0,
  pageSize: 20,
  hasMore: true,
}

function TwilioNumbers({ onSwitchTab }) {
  const [selectedKey, setSelectedKey] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  // Debounce the setter so we don't hammer the query on every keystroke
  const debouncedSetSearchTerm = debounce(value => setSearchTerm(value), 300)
  const [selectedNumbers, setSelectedNumbers] = useState([])
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION)

  const queryClient = useQueryClient()

  // Reset pagination when the selected key changes
  useEffect(() => {
    if (selectedKey) {
      setPagination(DEFAULT_PAGINATION)
    }
  }, [selectedKey])

  const {
    data: encryptedKeys = [],
    isLoading: keysLoading,
    error: keysError,
  } = useQuery({
    queryKey: ['encrypted-keys'],
    queryFn: async () => {
      const response = await $fetch('/byot/get_keys', { method: 'GET' })
      return response.data.map(item => item.id)
    },
  })

  const { data: numbersData, error: numbersError, isLoading: numbersLoading } = useQuery({
    queryKey: [
      'twilio-numbers',
      selectedKey,
      pagination.page,
      pagination.pageSize,
      searchTerm,
    ],
    queryFn: async () => {
      if (!selectedKey)
        return { numbers: [] }

      const [twilioResponse, inboundData] = await Promise.all([
        $fetch('/byot/get_twilio', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            encrypted_key: selectedKey,
            page: pagination.page,
            page_size: pagination.pageSize,
            search: searchTerm,
          }),
        }),
        getInbound(selectedKey),
      ])

      const twilioNumbers
        = twilioResponse.data?.map(num => ({
          number: num,
          imported: inboundData.inbound_numbers.some(
            inbound => inbound.phone_number === num,
          ),
        })) || []

      // This shouldn't trigger this query again
      setPagination(prev => ({
        ...prev,
        hasMore: prev.pageSize < 1000 && twilioNumbers.length > 0,
      }))

      return {
        numbers: twilioNumbers,
      }
    },
    enabled: !!selectedKey,
  })

  const importMutation = useMutation({
    mutationFn: async (numbers) => {
      if (!selectedKey) {
        throw new Error('No key selected')
      }
      return await insertInbound(selectedKey, numbers)
    },
    onSuccess: (data, variables) => {
      if (data.status === 'success') {
        queryClient.invalidateQueries({ queryKey: ['twilio-numbers'] })
        setSelectedNumbers([])
        toast.success(
          `Successfully imported ${variables.length} Twilio numbers.`,
        )
      }
      else {
        throw new Error(data.message || 'Failed to import numbers')
      }
    },
    onError: (error) => {
      toast.error(`Failed to import selected numbers. Error: ${error.message}`)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (number) => {
      if (!selectedKey) {
        throw new Error('No key selected')
      }
      return await deleteInbound(selectedKey, number)
    },
    onSuccess: (data, number) => {
      if (data?.success) {
        queryClient.invalidateQueries({ queryKey: ['twilio-numbers'] })
        toast.success(`Successfully deleted ${number}`)
      }
      else {
        throw new Error(data?.message || 'Failed to delete number')
      }
    },
    onError: (error, number) => {
      toast.error(`Failed to delete ${number}. Error: ${error.message}`)
    },
  })

  const copyToClipboard = (key) => {
    navigator.clipboard.writeText(key).then(
      () => toast.success('Encrypted key copied to clipboard'),
      err => toast.error(`Failed to copy encrypted key: ${err.message}`),
    )
  }

  const handleImportSelected = () => {
    const numbersToImport = selectedNumbers.filter(
      num => !numbersData?.numbers.find(n => n.number === num)?.imported,
    )
    if (numbersToImport.length > 0) {
      importMutation.mutate(numbersToImport)
    }
  }

  const handleDelete = (number) => {
    deleteMutation.mutate(number)
  }

  const filteredNumbers
    = numbersData?.numbers?.filter(num =>
      num.number.toLowerCase().includes(searchTerm.toLowerCase()),
    ) || []

  const loadAllNumbers = () => {
    setPagination({
      page: 0,
      pageSize: 1000,
      hasMore: false,
    })
  }

  const loading = keysLoading || importMutation.isPending || deleteMutation.isPending
  const error = keysError || numbersError

  const handleGenerateNewKey = () => {
    onSwitchTab(1) // Switch to the "Generate Key" tab
  }

  return (
    <div className="mx-auto bg-white">
      <h1 className="mb-5 border-b pb-2.5 text-xl font-bold text-gray-800">
        Twilio Number Management
      </h1>

      {error && (
        <div className={`
          mb-4 rounded-r border-l-4 border-red-400 bg-red-50 p-2.5
        `}
        >
          <p className="text-red-700">{error}</p>
        </div>
      )}

      <div className="mb-8">
        <h2 className="mb-2.5 text-lg font-semibold text-gray-700">
          Step 1: Select or Generate Encrypted Key
        </h2>
        <p className="mb-2.5 text-gray-600">
          An encrypted key is required to securely manage your Twilio numbers.
          Select an existing key or generate a new one.
        </p>
        {keysLoading
          ? (
              <div className="fixed inset-0 h-full w-full">
                <GradientLoadingAnimation
                  variant="mintForest"
                  message="Loading encrypted keys..."
                />
              </div>
            )
          : encryptedKeys.length > 0
            ? (
                <>
                  <p className="mb-1.5 text-xs text-gray-500">
                    Select an encrypted key:
                  </p>
                  <div className={`
                    grid grid-cols-1 gap-2.5
                    sm:grid-cols-2
                    lg:grid-cols-3
                  `}
                  >
                    {encryptedKeys.map(key => (
                      <div
                        key={key}
                        className={`
                          rounded-md border p-2.5
                          ${
                      selectedKey === key
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200'
                      }
                          cursor-pointer shadow-md transition-colors
                          hover:border-blue-300
                        `}
                        onClick={() => {
                          setSelectedKey(key)
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-mono text-xs text-gray-600">
                            {key.slice(0, 4)}
                            ...
                            {key.slice(-4)}
                          </span>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation()
                              copyToClipboard(key)
                            }}
                            className={`
                              text-gray-400
                              hover:text-gray-600
                            `}
                            title="Copy encrypted key"
                          >
                            <Copy className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )
            : (
                <div className={`
                  rounded-r border-l-4 border-yellow-400 bg-yellow-50 p-2.5
                `}
                >
                  <p className="mb-1.5 text-yellow-700">
                    No encrypted keys found. Generate a key to get started.
                  </p>
                  <button
                    type="button"
                    onClick={handleGenerateNewKey}
                    className={`
                      rounded bg-yellow-500 px-2.5 py-1.5 text-white
                      hover:bg-yellow-600
                      focus:ring-2 focus:ring-yellow-500/50 focus:outline-hidden
                    `}
                  >
                    Generate New Key
                  </button>
                </div>
              )}
      </div>

      {selectedKey && (
        <>
          <div className="mb-8">
            <h2 className="mb-2.5 text-lg font-semibold text-gray-700">
              Step 2: Manage Twilio Numbers
            </h2>
            <p className="mb-2.5 text-gray-600">
              View, search, and manage your Twilio numbers. You can import new
              numbers or remove existing ones.
            </p>
            <div className="mb-2.5 flex items-center">
              <input
                type="text"
                placeholder="Search numbers"
                onChange={e => debouncedSetSearchTerm(e.target.value)}
                className={`
                  grow rounded-l-md border border-gray-300 p-1.5
                  focus:ring-2 focus:ring-blue-500 focus:outline-hidden
                `}
              />
              <div className="rounded-r-md bg-gray-100 p-1.5">
                <Search className="h-5 w-5" />
              </div>
            </div>
            <div className={`
              overflow-hidden rounded-md border border-gray-200 bg-white
              shadow-lg
            `}
            >
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className={`
                      px-4 py-2 text-left text-2xs font-medium tracking-wider
                      text-gray-500 uppercase
                    `}
                    >
                      Twilio Number
                    </th>
                    <th className={`
                      px-4 py-2 text-left text-2xs font-medium tracking-wider
                      text-gray-500 uppercase
                    `}
                    >
                      Import Status
                    </th>
                    <th className={`
                      px-4 py-2 text-right text-2xs font-medium tracking-wider
                      text-gray-500 uppercase
                    `}
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="relative divide-y divide-gray-200 bg-white">
                  {numbersLoading
                    ? (
                        <div className="h-60">
                          <div className={`
                            absolute top-0 left-0 z-10 flex h-full w-full
                            items-center justify-center
                          `}
                          >
                            <GradientLoadingAnimation
                              variant="mintForest"
                              message="Loading Twilio numbers..."
                            />
                          </div>
                        </div>
                      )
                    : filteredNumbers?.map(num => (
                        <tr key={num.number}>
                          <td className="px-4 py-2.5 whitespace-nowrap">
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                checked={selectedNumbers.includes(num.number)}
                                onChange={() => {
                                  setSelectedNumbers(prev =>
                                    prev.includes(num.number)
                                      ? prev.filter(n => n !== num.number)
                                      : [...prev, num.number],
                                  )
                                }}
                                className={`
                                  mr-2 h-2.5 w-2.5 rounded-sm border-gray-300
                                  text-blue-600
                                  focus:ring-blue-500
                                `}
                                disabled={num.imported}
                              />
                              <span className="font-medium text-gray-900">
                                {num.number}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-2.5 whitespace-nowrap">
                            <span
                              className={`
                                inline-flex rounded-full px-1.5 text-2xs
                                leading-3 font-semibold
                                ${
                        num.imported
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                        }
                              `}
                            >
                              {num.imported ? 'Imported' : 'Not Imported'}
                            </span>
                          </td>
                          <td className={`
                            px-4 py-2.5 text-right text-xs font-medium
                            whitespace-nowrap
                          `}
                          >
                            {num.imported && (
                              <button
                                type="button"
                                onClick={() => handleDelete(num.number)}
                                className={`
                                  text-red-600
                                  hover:text-red-900
                                `}
                                title="Remove number"
                              >
                                <Trash />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                </tbody>
              </table>
            </div>
            {pagination.hasMore && (
              <div className="mt-2.5 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={loadAllNumbers}
                  className={`
                    rounded bg-gray-200 px-2.5 py-1.5 text-gray-700
                    hover:bg-gray-300
                    focus:ring-2 focus:ring-gray-500/50 focus:outline-hidden
                  `}
                >
                  Load All Numbers
                </button>
              </div>
            )}
            <div className="mt-2.5 flex items-center justify-between">
              <span className="text-xs text-gray-500">
                {selectedNumbers.length}
                {' '}
                number
                {selectedNumbers.length !== 1 ? 's' : ''}
                {' '}
                selected
              </span>
              <button
                type="button"
                onClick={handleImportSelected}
                disabled={selectedNumbers.length === 0 || loading}
                className={`
                  rounded bg-blue-600 px-2.5 py-1.5 text-white
                  hover:bg-blue-700
                  focus:ring-2 focus:ring-blue-500/50 focus:outline-hidden
                  disabled:cursor-not-allowed disabled:opacity-50
                `}
              >
                Import Selected Numbers
              </button>
            </div>
          </div>

          <div className="mb-8">
            <h2 className="mb-2.5 text-lg font-semibold text-gray-700">
              Step 3: Twilio Configuration
            </h2>
            <p className="mb-2.5 text-gray-600">
              Set up your Twilio account configuration for the selected
              encrypted key.
            </p>
            <ConfigurationSection encryptedKey={selectedKey} />
          </div>
        </>
      )}
    </div>
  )
}
export default TwilioNumbers
